import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Platform, ScrollView, View} from 'react-native'
import {AtUri, AppBskyEmbedVideo, AppBskyFeedPost} from '@atproto/api'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'
import {type NativeStackNavigationProp} from '@react-navigation/native-stack'
import {Image} from 'expo-image'

import {uploadBlob} from '#/lib/api'
import {type CommonNavigatorParams} from '#/lib/routes/types'
import {
  isTVSegmenterError,
  segmentVideo,
  type TVSegmentFile,
} from '#/lib/tv/segmenter'
import {createMasterRecord, uploadThumbnailBlob} from '#/lib/tv/tvApi'
import {
  TV_MASTER_PREFIX,
  TV_SEGMENT_DURATION_PREFIX,
  TV_SEGMENT_MARKER,
  TV_TOTAL_DURATION_PREFIX,
  TV_VIDEO_MARKER,
  type TVSegment,
} from '#/lib/tv/tvTypes'
import {
  clearUploadDraft,
  loadUploadDraft,
  saveUploadDraft,
  type UploadDraft,
} from '#/lib/tv/uploadDraft'
import {useAgent, useRequireAuth} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Input as TextFieldInput, LabelText} from '#/components/forms/TextField'
import * as Layout from '#/components/Layout'
import * as toast from '#/components/Toast'
import {Text} from '#/components/Typography'

type UploadStage = 'idle' | 'segmenting' | 'uploading' | 'creating' | 'done'
type UploadFailureStep =
  | 'ffmpeg_init'
  | 'segmenting'
  | 'upload_blob'
  | 'post_segment'
  | 'post_videopage'

type UploadErrorState = {
  step: UploadFailureStep
  message: string
  url?: string
  status?: number
  stack?: string
}

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function stripExtension(name: string) {
  return name.replace(/\.[^/.]+$/, '')
}

function buildVideoPageText({
  title,
  description,
  segmentDurationMs,
  totalDurationMs,
  masterUri,
}: {
  title: string
  description?: string
  segmentDurationMs: number
  totalDurationMs: number
  masterUri?: string
}) {
  const lines = [title.trim()]
  if (description) {
    lines.push(description.trim())
  }
  lines.push(TV_VIDEO_MARKER)
  lines.push(`${TV_SEGMENT_DURATION_PREFIX}${Math.round(segmentDurationMs)}`)
  lines.push(`${TV_TOTAL_DURATION_PREFIX}${Math.round(totalDurationMs)}`)
  if (masterUri) {
    lines.push(`${TV_MASTER_PREFIX} ${masterUri}`)
  }
  return lines.join('\n')
}

const DEFAULT_SEGMENT_SECONDS = 30

function getSegmentSeconds() {
  if (typeof process === 'undefined') return DEFAULT_SEGMENT_SECONDS
  const raw = process.env.EXPO_PUBLIC_TELLUS_TV_SEGMENT_SECONDS
  if (!raw) return DEFAULT_SEGMENT_SECONDS
  const parsed = Number(raw)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_SEGMENT_SECONDS
  }
  return parsed
}

function formatSegmentLabel(seconds: number) {
  if (seconds >= 60 && seconds % 60 === 0) {
    const minutes = seconds / 60
    return `${minutes} min`
  }
  return `${seconds}s`
}

function formatDuration(totalMs?: number) {
  if (!totalMs || totalMs <= 0) return '--:--'
  const totalSec = Math.round(totalMs / 1000)
  const hours = Math.floor(totalSec / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = String(totalSec % 60).padStart(2, '0')
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
  }
  return `${minutes}:${seconds}`
}

async function captureThumbnailFromVideo(file: File): Promise<{
  blob: Blob
  url: string
}> {
  if (typeof document === 'undefined') {
    throw new Error('Thumbnail generation is only available on web.')
  }
  const objectUrl = URL.createObjectURL(file)
  try {
    const video = document.createElement('video')
    video.src = objectUrl
    video.muted = true
    video.playsInline = true
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve()
      video.onerror = () => reject(new Error('Failed to load video file.'))
    })
    const seekTo = Math.min(1, Math.max(0, video.duration ? Math.min(1, video.duration / 2) : 1))
    video.currentTime = seekTo
    await new Promise<void>((resolve, reject) => {
      video.onseeked = () => resolve()
      video.onerror = () => reject(new Error('Failed to seek video frame.'))
    })
    const targetWidth = 1280
    const targetHeight = 720
    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Canvas is not supported in this browser.')
    }
    ctx.fillStyle = '#000'
    ctx.fillRect(0, 0, targetWidth, targetHeight)
    const videoWidth = video.videoWidth || targetWidth
    const videoHeight = video.videoHeight || targetHeight
    const scale = Math.min(targetWidth / videoWidth, targetHeight / videoHeight)
    const drawWidth = Math.round(videoWidth * scale)
    const drawHeight = Math.round(videoHeight * scale)
    const dx = Math.round((targetWidth - drawWidth) / 2)
    const dy = Math.round((targetHeight - drawHeight) / 2)
    ctx.drawImage(video, dx, dy, drawWidth, drawHeight)
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        result => {
          if (result) resolve(result)
          else reject(new Error('Failed to generate thumbnail.'))
        },
        'image/jpeg',
        0.9,
      )
    })
    const url = URL.createObjectURL(blob)
    return {blob, url}
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

function buildUploadError(
  err: unknown,
  fallbackStep: UploadFailureStep,
): UploadErrorState {
  if (isTVSegmenterError(err)) {
    return {
      step: err.step === 'ffmpeg_init' ? 'ffmpeg_init' : 'segmenting',
      message: err.message,
      url: err.url,
      status: err.status,
      stack: err.stack,
    }
  }
  const errorStatus =
    err && typeof err === 'object' ? (err as {status?: number}).status : undefined
  const errorUrl =
    err && typeof err === 'object' ? (err as {url?: string}).url : undefined
  const errorData =
    err && typeof err === 'object' ? (err as {data?: unknown}).data : undefined
  const serverMessage =
    errorData && typeof errorData === 'object'
      ? (errorData as {message?: string; error?: string}).message ||
        (errorData as {message?: string; error?: string}).error
      : undefined
  let message =
    err instanceof Error ? err.message : 'Upload failed unexpectedly.'
  if (errorStatus === 401 && typeof serverMessage === 'string') {
    const normalized = serverMessage.toLowerCase()
    if (normalized.includes('invalid token audience')) {
      message =
        'Upload rejected: token audience mismatch. Please re-authenticate.'
    } else if (normalized.includes('invalid token lexicon method')) {
      message = serverMessage
    }
  }
  return {
    step: fallbackStep,
    message,
    url: errorUrl,
    status: errorStatus,
    stack: err instanceof Error ? err.stack : undefined,
  }
}

export function TellusTVUploadScreen() {
  const t = useTheme()
  const {_} = useLingui()
  const agent = useAgent()
  const requireAuth = useRequireAuth()
  const navigation =
    useNavigation<NativeStackNavigationProp<CommonNavigatorParams>>()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const thumbnailInputRef = useRef<HTMLInputElement>(null)
  const segmentSeconds = useMemo(() => getSegmentSeconds(), [])
  const segmentLabel = useMemo(
    () => formatSegmentLabel(segmentSeconds),
    [segmentSeconds],
  )
  const [draft, setDraft] = useState<UploadDraft | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<UploadStage>('idle')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<UploadErrorState | null>(null)
  const [segmentingProgress, setSegmentingProgress] = useState(0)
  const [segmentsTotal, setSegmentsTotal] = useState(0)
  const [uploadedCount, setUploadedCount] = useState(0)
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState<number | null>(
    null,
  )
  const [currentSegmentProgress, setCurrentSegmentProgress] = useState(0)
  const [posterUrl, setPosterUrl] = useState<string | null>(null)
  const [thumbnailBlob, setThumbnailBlob] = useState<Blob | null>(null)
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | null>(
    null,
  )
  const [thumbnailMime, setThumbnailMime] = useState<string | null>(null)
  const [thumbnailGenerating, setThumbnailGenerating] = useState(false)

  useEffect(() => {
    let active = true
    loadUploadDraft().then(next => {
      if (active) {
        setDraft(next)
        if (next?.title) setTitle(next.title)
        if (next?.description) setDescription(next.description)
      }
    })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    return () => {
      if (posterUrl) {
        URL.revokeObjectURL(posterUrl)
      }
    }
  }, [posterUrl])

  useEffect(() => {
    return () => {
      if (thumbnailPreviewUrl) {
        URL.revokeObjectURL(thumbnailPreviewUrl)
      }
    }
  }, [thumbnailPreviewUrl])

  const selectThumbnail = useCallback(() => {
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.click()
    }
  }, [])

  const onPickThumbnail = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return
      if (thumbnailPreviewUrl) {
        URL.revokeObjectURL(thumbnailPreviewUrl)
      }
      setThumbnailBlob(file)
      setThumbnailMime(file.type || 'image/jpeg')
      setThumbnailPreviewUrl(URL.createObjectURL(file))
    },
    [thumbnailPreviewUrl],
  )

  const onGenerateThumbnail = useCallback(async () => {
    if (!file) {
      toast.show(_(msg`Select a video first.`), {type: 'info'})
      return
    }
    if (thumbnailGenerating) return
    setThumbnailGenerating(true)
    try {
      const generated = await captureThumbnailFromVideo(file)
      if (thumbnailPreviewUrl) {
        URL.revokeObjectURL(thumbnailPreviewUrl)
      }
      setThumbnailBlob(generated.blob)
      setThumbnailMime('image/jpeg')
      setThumbnailPreviewUrl(generated.url)
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : _(msg`Failed to generate thumbnail.`),
        {type: 'error'},
      )
    } finally {
      setThumbnailGenerating(false)
    }
  }, [file, thumbnailGenerating, thumbnailPreviewUrl, _])

  const sharedArrayBufferMissing =
    Platform.OS === 'web' &&
    typeof (globalThis as {SharedArrayBuffer?: unknown}).SharedArrayBuffer ===
      'undefined'

  const reportUploadError = useCallback(
    (err: unknown, fallbackStep: UploadFailureStep) => {
      const details = buildUploadError(err, fallbackStep)
      setUploadError(details)
    },
    [],
  )

  const overallUploadProgress = useMemo(() => {
    if (!segmentsTotal) return 0
    return (uploadedCount + currentSegmentProgress) / segmentsTotal
  }, [currentSegmentProgress, segmentsTotal, uploadedCount])

  const totalDurationMs = useMemo(() => {
    if (!draft?.uploadedSegments?.length) return undefined
    return draft.uploadedSegments.reduce((acc, seg) => acc + seg.durationMs, 0)
  }, [draft?.uploadedSegments])

  const onPickFile = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const nextFile = evt.target.files?.[0]
      if (!nextFile) return
      setFile(nextFile)
      setThumbnailBlob(null)
      setThumbnailMime(null)
      if (thumbnailPreviewUrl) {
        URL.revokeObjectURL(thumbnailPreviewUrl)
      }
      setThumbnailPreviewUrl(null)
      if (!title) {
        setTitle(stripExtension(nextFile.name))
      }
    },
    [thumbnailPreviewUrl, title],
  )

  const selectFile = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const startUpload = useCallback(
    async (resume: boolean) => {
      if (Platform.OS !== 'web') {
        toast.show(_(msg`Upload is only available on web right now.`), {
          type: 'info',
        })
        return
      }
      if (!file) {
        toast.show(_(msg`Select a video file first.`), {type: 'info'})
        return
      }
      if (!title.trim()) {
        toast.show(_(msg`Add a title for your video.`), {type: 'info'})
        return
      }
      if (resume && draft) {
        if (draft.fileName !== file.name || draft.fileSize !== file.size) {
          setValidationError(
            _(msg`Selected file does not match the saved draft.`),
          )
          return
        }
      }

      setValidationError(null)
      setUploadError(null)
      setStatus('segmenting')
      setSegmentingProgress(0)
      setSegmentsTotal(0)
      setUploadedCount(0)
      setCurrentSegmentIndex(null)
      setCurrentSegmentProgress(0)
      setPosterUrl(null)

      let segments: TVSegmentFile[] = []
      let posterBlob: Blob | null = null
      let posterObjectUrl: string | null = null
      let width = 0
      let height = 0
      let currentStep: UploadFailureStep = 'segmenting'

      try {
        let segmentResult: Awaited<ReturnType<typeof segmentVideo>>
        try {
          segmentResult = await segmentVideo(file, {
            segmentSeconds,
            onProgress: setSegmentingProgress,
          })
        } catch (err) {
          setStatus('idle')
          reportUploadError(err, 'segmenting')
          return
        }

        ;({segments, posterBlob, posterObjectUrl, width, height} = segmentResult)
        if (!segments.length || !posterBlob) {
          setStatus('idle')
          reportUploadError(
            new Error('Segmenting failed to produce usable data.'),
            'segmenting',
          )
          return
        }

        if (
          resume &&
          draft?.segmentsTotal &&
          draft.segmentsTotal !== segments.length
        ) {
          setStatus('idle')
          setValidationError(
            _(msg`Saved draft does not match the selected file segments.`),
          )
          return
        }
        if (
          resume &&
          draft?.segmentSeconds &&
          draft.segmentSeconds !== segmentSeconds
        ) {
          setStatus('idle')
          setValidationError(
            _(msg`Saved draft does not match the selected segment length.`),
          )
          return
        }

        setPosterUrl(posterObjectUrl)
        setSegmentsTotal(segments.length)
        const totalDurationMs = segments.reduce(
          (acc, seg) => acc + seg.durationMs,
          0,
        )

        const baseDraft: UploadDraft = {
          version: 1,
          fileName: file.name,
          fileSize: file.size,
          title: title.trim(),
          description: description.trim() || undefined,
          createdAt: Date.now(),
          segmentsTotal: segments.length,
          segmentSeconds,
          uploadedSegments: resume ? draft?.uploadedSegments || [] : [],
        }

        await saveUploadDraft(baseDraft)
        setDraft(baseDraft)

        const uploadedMap = new Map(
          (baseDraft.uploadedSegments || [])
            .filter(seg => !!seg.cid)
            .map(seg => [seg.index, seg]),
        )
        setUploadedCount(uploadedMap.size)
        setStatus('uploading')
        currentStep = 'upload_blob'

        const aspectRatio =
          width > 0 && height > 0
            ? {width: Math.round(width), height: Math.round(height)}
            : undefined

        const segmentRefs: TVSegment[] = []
        let rootRecord: AppBskyFeedPost.Record | null = null
        let rootUri: string | null = null

        const uploadSegment = async (
          segment: TVSegmentFile,
          order: number,
          rootRef?: {uri: string; cid: string},
          parentRef?: {uri: string; cid: string},
        ): Promise<TVSegment> => {
          if (!agent?.assertDid) {
            throw new Error('Missing session DID for upload.')
          }
          const blob = new Blob([segment.bytes], {type: segment.mimeType})
          const uploadRes = await uploadBlob(agent, blob, segment.mimeType)
          const video = uploadRes.data.blob
          const isRoot = order === 0
          const record: AppBskyFeedPost.Record = {
            $type: 'app.bsky.feed.post',
            createdAt: new Date().toISOString(),
            text: isRoot
              ? buildVideoPageText({
                  title: title.trim(),
                  description: description.trim() || undefined,
                  segmentDurationMs: segment.durationMs,
                  totalDurationMs,
                })
              : `${TV_SEGMENT_MARKER}\n${TV_SEGMENT_DURATION_PREFIX}${Math.round(
                  segment.durationMs,
                )}`,
            embed: {
              $type: 'app.bsky.embed.video',
              video,
              aspectRatio,
            } as AppBskyEmbedVideo.Main,
            reply:
              rootRef && parentRef
                ? {
                    root: rootRef,
                    parent: parentRef,
                  }
                : undefined,
          }
          if (isRoot) {
            rootRecord = record
          }

          let res
          try {
            currentStep = 'post_segment'
            res = await agent.com.atproto.repo.createRecord({
              repo: agent.assertDid,
              collection: 'app.bsky.feed.post',
              record,
            })
          } catch (err) {
            const wrapped = err instanceof Error ? err : new Error('Failed to post segment.')
            ;(wrapped as {step?: UploadFailureStep}).step = 'post_segment'
            throw wrapped
          }

          if (isRoot) {
            rootUri = res.data.uri
          }
          return {
            uri: res.data.uri,
            cid: res.data.cid,
            durationMs: segment.durationMs,
            mimeType: segment.mimeType,
            byteLength: segment.size,
            order,
          }
        }

        for (let i = 0; i < segments.length; i++) {
          const existing = uploadedMap.get(i)
          if (existing && existing.cid) {
            const durationMs = existing.durationMs || segments[i].durationMs
            segmentRefs.push({
              uri: existing.uri,
              cid: existing.cid,
              durationMs,
              mimeType: existing.mimeType || segments[i].mimeType,
              byteLength: existing.byteLength || segments[i].size,
              order: i,
            })
            continue
          }

          setCurrentSegmentIndex(i)
          setCurrentSegmentProgress(0)

          let uploadedSegment: TVSegment | undefined
          let attempts = 0
          while (!uploadedSegment && attempts < 3) {
            attempts++
            try {
              const root =
                segmentRefs.length > 0
                  ? {
                      uri: segmentRefs[0].uri,
                      cid: segmentRefs[0].cid!,
                    }
                  : undefined
              const parent =
                segmentRefs.length > 0
                  ? {
                      uri: segmentRefs[segmentRefs.length - 1].uri,
                      cid: segmentRefs[segmentRefs.length - 1].cid!,
                    }
                  : undefined
              uploadedSegment = await uploadSegment(segments[i], i, root, parent)
            } catch (err) {
              if (attempts >= 3) {
                setStatus('idle')
                const step =
                  err && typeof err === 'object'
                    ? (err as {step?: UploadFailureStep}).step
                    : undefined
                reportUploadError(err, step || 'upload_blob')
                return
              }
              await sleep(1500 * attempts)
            }
          }

          if (!uploadedSegment) {
            setStatus('idle')
            reportUploadError(new Error('Failed to upload segment.'), 'upload_blob')
            return
          }

          setCurrentSegmentProgress(1)
          segmentRefs.push(uploadedSegment)
          uploadedMap.set(i, {
            index: i,
            uri: uploadedSegment.uri,
            cid: uploadedSegment.cid,
            durationMs: uploadedSegment.durationMs,
            mimeType: uploadedSegment.mimeType,
            byteLength: uploadedSegment.byteLength,
          })
          const nextDraft = {
            ...baseDraft,
            uploadedSegments: Array.from(uploadedMap.values()),
          }
          await saveUploadDraft(nextDraft)
          setDraft(nextDraft)
          setUploadedCount(uploadedMap.size)
        }

        setCurrentSegmentIndex(null)
        setCurrentSegmentProgress(0)
        setStatus('creating')
        currentStep = 'post_videopage'
        if (!segmentRefs[0] || !rootUri) {
          setStatus('idle')
          reportUploadError(
            new Error('No segments were uploaded.'),
            'post_videopage',
          )
          return
        }
        const thumbnailCandidate = thumbnailBlob || posterBlob
        let thumbnailCid: string | undefined
        let thumbnailMimeValue: string | undefined
        if (thumbnailCandidate) {
          const res = await uploadThumbnailBlob(
            agent,
            thumbnailCandidate,
            thumbnailMime || 'image/jpeg',
          )
          thumbnailCid = res.cid
          thumbnailMimeValue = res.mime
        }

        const master = await createMasterRecord(agent, {
          title: title.trim(),
          description: description.trim() || undefined,
          createdAt: new Date().toISOString(),
          segments: segmentRefs,
          totalDurationMs,
          segmentSeconds,
          thumbnailCid,
          thumbnailMime: thumbnailMimeValue,
          thumbnailAspect: '16:9',
        })

        if (rootRecord) {
          const updatedRecord: AppBskyFeedPost.Record = {
            ...rootRecord,
            text: buildVideoPageText({
              title: title.trim(),
              description: description.trim() || undefined,
              segmentDurationMs: segmentRefs[0].durationMs,
              totalDurationMs,
              masterUri: master.uri,
            }),
          }
          const rootRkey = new AtUri(rootUri).rkey
          await agent.com.atproto.repo.putRecord({
            repo: agent.assertDid,
            collection: 'app.bsky.feed.post',
            rkey: rootRkey,
            record: updatedRecord,
          })
        }

        const videoPage = {data: {uri: rootUri}}

        await clearUploadDraft()
        setDraft(null)
        setStatus('done')
        toast.show(_(msg`Video published.`), {type: 'success'})
        navigation.navigate('TellusTVWatch', {
          uriEncoded: encodeURIComponent(videoPage.data.uri),
        })
      } catch (err) {
        setStatus('idle')
        reportUploadError(err, currentStep)
      } finally {
        segments.forEach(segment => {
          if (segment.objectUrl) {
            URL.revokeObjectURL(segment.objectUrl)
          }
        })
      }
    },
    [
      agent,
      description,
      draft,
      file,
      navigation,
      reportUploadError,
      segmentSeconds,
      thumbnailBlob,
      thumbnailMime,
      title,
      _,
    ],
  )

  if (Platform.OS !== 'web') {
    return (
      <Layout.Screen testID="tellusTVUploadScreen">
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>Tellus TV</Layout.Header.TitleText>
          </Layout.Header.Content>
        </Layout.Header.Outer>
        <View style={[a.px_lg, a.py_md]}>
          <Text>{_(msg`Upload is only available on web right now.`)}</Text>
        </View>
      </Layout.Screen>
    )
  }

  const isBusy = status === 'segmenting' || status === 'uploading' || status === 'creating'

  return (
    <Layout.Screen testID="tellusTVUploadScreen">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>Tellus TV</Layout.Header.TitleText>
        </Layout.Header.Content>
      </Layout.Header.Outer>
      <ScrollView
        contentContainerStyle={[a.px_lg, a.pb_xl, a.gap_lg]}
        showsVerticalScrollIndicator={false}>
        <View style={[a.gap_sm]}>
          <Text style={[a.text_lg, a.font_bold]}>
            <Trans>Upload a long video</Trans>
          </Text>
          <Text style={[t.atoms.text_contrast_medium]}>
            <Trans>
              We split it into {segmentLabel} segments for Tellus TV.
            </Trans>
          </Text>
        </View>

        <View style={[a.gap_sm]}>
          <LabelText>{_(msg`Video file`)}</LabelText>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            style={a.hidden}
            onChange={onPickFile}
            disabled={isBusy}
            aria-disabled={isBusy}
          />
          <Button
            variant="solid"
            color="primary"
            size="large"
            label={_(msg`Select video file`)}
            onPress={selectFile}
            disabled={isBusy}>
            <ButtonText>
              {file ? file.name : _(msg`Select video file`)}
            </ButtonText>
          </Button>
        </View>

        <View style={[a.gap_sm]}>
          <LabelText>{_(msg`Title`)}</LabelText>
          <TextFieldInput
            value={title}
            onChangeText={setTitle}
            editable={!isBusy}
            accessibilityLabel={_(msg`Title`)}
          />
        </View>

        <View style={[a.gap_sm]}>
          <LabelText>{_(msg`Description`)}</LabelText>
          <TextFieldInput
            value={description}
            onChangeText={setDescription}
            editable={!isBusy}
            accessibilityLabel={_(msg`Description`)}
            multiline
            numberOfLines={4}
          />
        </View>

        {sharedArrayBufferMissing ? (
          <View style={[a.rounded_md, a.p_md, t.atoms.bg_contrast_25]}>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {
                'Video segmentation requires a modern browser configuration. Try Chrome/Edge or disable strict cross-origin isolation constraints.'
              }
            </Text>
          </View>
        ) : null}

        <View style={[a.gap_sm]}>
          <LabelText>{_(msg`Thumbnail`)}</LabelText>
          <input
            ref={thumbnailInputRef}
            type="file"
            accept="image/*"
            style={a.hidden}
            onChange={onPickThumbnail}
            disabled={isBusy}
            aria-disabled={isBusy}
          />
          <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
            <Button
              variant="outline"
              color="secondary"
              size="small"
              label={_(msg`Choose thumbnail`)}
              onPress={selectThumbnail}
              disabled={isBusy}>
              <ButtonText>{_(msg`Choose thumbnail`)}</ButtonText>
            </Button>
            <Button
              variant="ghost"
              color="secondary"
              size="small"
              label={_(msg`Generate from video`)}
              onPress={onGenerateThumbnail}
              disabled={isBusy || !file || thumbnailGenerating}>
              <ButtonText>
                {thumbnailGenerating
                  ? _(msg`Generating…`)
                  : _(msg`Generate from video`)}
              </ButtonText>
            </Button>
          </View>
          {thumbnailPreviewUrl ? (
            <View
              style={[
                a.rounded_md,
                a.overflow_hidden,
                {backgroundColor: t.atoms.bg_contrast_25.backgroundColor},
              ]}>
              <Image
                source={{uri: thumbnailPreviewUrl}}
                style={[a.w_full, {maxWidth: 420, aspectRatio: 16 / 9}]}
                accessibilityIgnoresInvertColors
              />
            </View>
          ) : null}
        </View>

        {posterUrl ? (
          <View style={[a.gap_sm]}>
            <LabelText>{_(msg`Generated preview`)}</LabelText>
            <View
              style={[
                a.rounded_md,
                a.overflow_hidden,
                {backgroundColor: t.atoms.bg_contrast_25.backgroundColor},
              ]}>
              <Image
                source={{uri: posterUrl}}
                style={[a.w_full, {maxWidth: 420, aspectRatio: 16 / 9}]}
                accessibilityIgnoresInvertColors
              />
            </View>
          </View>
        ) : null}

        {draft?.uploadedSegments?.length ? (
          <View style={[a.rounded_md, a.p_md, t.atoms.bg_contrast_25]}>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {_(msg`Draft saved:`)} {draft.uploadedSegments.length}/
              {draft.segmentsTotal || 0} segments uploaded.
            </Text>
            {totalDurationMs ? (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {_(msg`Total duration:`)} {formatDuration(totalDurationMs)}
              </Text>
            ) : null}
          </View>
        ) : null}

        {validationError ? (
          <View style={[a.rounded_md, a.p_md, t.atoms.bg_contrast_25]}>
            <Text style={[a.text_sm, {color: t.palette.negative_500}]}>
              {validationError}
            </Text>
          </View>
        ) : null}

        {uploadError ? (
          <View style={[a.rounded_md, a.p_md, t.atoms.bg_contrast_25]}>
            <Text style={[a.text_sm, {color: t.palette.negative_500}]}>
              {uploadError.step === 'ffmpeg_init'
                ? 'FFmpeg assets failed to load'
                : 'Upload failed'}
            </Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {`Step: ${uploadError.step}`}
            </Text>
            {uploadError.url ? (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {`URL: ${uploadError.url}`}
              </Text>
            ) : null}
            {uploadError.status ? (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {`Status: ${uploadError.status}`}
              </Text>
            ) : null}
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {`Message: ${uploadError.message}`}
            </Text>
            {uploadError.step === 'ffmpeg_init' ? (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {'Ensure /ffmpeg/* is served (check build output).'}
              </Text>
            ) : null}
            {__DEV__ && uploadError.stack ? (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {`Stack: ${uploadError.stack}`}
              </Text>
            ) : null}
          </View>
        ) : null}

        {status === 'segmenting' ? (
          <ProgressRow
            label={_(msg`Segmenting video`)}
            progress={segmentingProgress}
          />
        ) : null}

        {status === 'uploading' ? (
          <ProgressRow
            label={`Uploading segment ${
              currentSegmentIndex !== null ? currentSegmentIndex + 1 : 1
            }/${segmentsTotal || 0}`}
            progress={overallUploadProgress}
          />
        ) : null}

        {status === 'creating' ? (
          <ProgressRow label={_(msg`Finalizing thread`)} progress={0.9} />
        ) : null}

        <View style={[a.flex_row, a.flex_wrap, a.gap_md]}>
          <Button
            variant="solid"
            color="primary"
            size="large"
            label={_(msg`Start upload`)}
            onPress={() => requireAuth(() => startUpload(false))}
            disabled={isBusy}>
            <ButtonText>{_(msg`Start upload`)}</ButtonText>
          </Button>
          {draft ? (
            <Button
              variant="outline"
              color="secondary"
              size="large"
              label={_(msg`Resume draft`)}
              onPress={() => requireAuth(() => startUpload(true))}
              disabled={isBusy || !file}>
              <ButtonText>{_(msg`Resume draft`)}</ButtonText>
            </Button>
          ) : null}
          {draft ? (
            <Button
              variant="ghost"
              color="secondary"
              size="large"
              label={_(msg`Discard draft`)}
              onPress={async () => {
                await clearUploadDraft()
                setDraft(null)
                setValidationError(null)
                setUploadError(null)
                setUploadedCount(0)
                setSegmentsTotal(0)
                setPosterUrl(null)
                setThumbnailBlob(null)
                setThumbnailMime(null)
                if (thumbnailPreviewUrl) {
                  URL.revokeObjectURL(thumbnailPreviewUrl)
                }
                setThumbnailPreviewUrl(null)
              }}
              disabled={isBusy}>
              <ButtonText>{_(msg`Discard draft`)}</ButtonText>
            </Button>
          ) : null}
        </View>
      </ScrollView>
    </Layout.Screen>
  )
}

function ProgressRow({label, progress}: {label: string; progress: number}) {
  const t = useTheme()
  const clamped = Math.max(0, Math.min(1, progress))
  return (
    <View style={[a.gap_sm]}>
      <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>{label}</Text>
      <View
        style={[
          a.rounded_md,
          {height: 6, backgroundColor: t.atoms.bg_contrast_25.backgroundColor},
        ]}>
        <View
          style={[
            {
              width: `${clamped * 100}%`,
              height: 6,
              backgroundColor: t.palette.primary_500,
              borderRadius: 999,
            },
          ]}
        />
      </View>
    </View>
  )
}
